import { cva } from "class-variance-authority"
import type * as React from "react"

function updateFieldControlPointer(event: React.PointerEvent<HTMLElement>) {
  if (event.pointerType === "touch") {
    return
  }

  const bounds = event.currentTarget.getBoundingClientRect()
  event.currentTarget.style.setProperty("--fl-field-control-pointer-x", `${event.clientX - bounds.left}px`)
  event.currentTarget.style.setProperty("--fl-field-control-pointer-y", `${event.clientY - bounds.top}px`)
}

const fieldControlFocusRingStyles =
  "outline-none focus-visible:relative focus-visible:z-10 focus-visible:outline-solid focus-visible:outline-[length:var(--fl-focus-ring-width)] focus-visible:outline-[color:var(--fl-color-focus-ring-outlined)] focus-visible:outline-offset-0"

const fieldControlFocusStyles = `${fieldControlFocusRingStyles} focus-visible:!border-[var(--fl-color-border-outlined-focus)]`

const fieldControlStateStyles =
  "hover:border-[var(--fl-color-border-outlined-hover)] has-[:focus-visible]:z-10 has-[:focus-visible]:outline-solid has-[:focus-visible]:outline-[length:var(--fl-focus-ring-width)] has-[:focus-visible]:outline-[color:var(--fl-color-focus-ring-outlined)] has-[:focus-visible]:outline-offset-0 has-[:focus-visible]:!border-[var(--fl-color-border-outlined-focus)] has-[[aria-invalid=true]]:border-[var(--fl-color-border-error-default)] has-[[aria-invalid=true]:focus-visible]:!border-[var(--fl-color-border-error-default)] group-data-[status=success]/field:border-[var(--fl-color-border-success-default)] has-[[data-field-control-input]:disabled]:border-[var(--fl-color-border-outlined-disabled)] has-[[data-field-control-input]:disabled]:bg-[var(--fl-color-background-outlined-disabled)] has-[[data-field-control-input]:disabled]:text-[var(--fl-color-content-default-disabled)]"

const fieldControlShellStyles = cva(
  `group/field-control relative flex h-[var(--fl-layout-input-fields-height)] w-full min-w-0 items-center overflow-clip rounded-[var(--fl-radius-4)] border border-[var(--fl-color-border-input-default)] bg-[var(--fl-color-background-secondary-default)] transition-[border-color,background-color,box-shadow] ${fieldControlStateStyles}`
)

const fieldControlInputStyles = cva(
  `min-w-0 select-text text-ui-12 font-normal placeholder:text-[var(--fl-color-content-default-secondary)] disabled:cursor-not-allowed disabled:pointer-events-none disabled:border-[var(--fl-color-border-outlined-disabled)] disabled:bg-[var(--fl-color-background-outlined-disabled)] disabled:text-[var(--fl-color-content-default-disabled)] disabled:placeholder:text-[var(--fl-color-content-default-disabled)] aria-invalid:border-[var(--fl-color-border-error-default)] group-data-[status=success]/field:border-[var(--fl-color-border-success-default)] [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none`,
  {
    variants: {
      appearance: {
        standalone: `h-[var(--fl-layout-input-fields-height)] w-full rounded-[var(--fl-radius-4)] border border-[var(--fl-color-border-input-default)] bg-[var(--fl-color-background-secondary-default)] px-[var(--fl-layout-input-fields-padding-horizontal)] py-[var(--fl-layout-input-fields-padding-vertical)] transition-[border-color,background-color,box-shadow] hover:border-[var(--fl-color-border-outlined-hover)] focus-visible:bg-[var(--fl-color-background-secondary-default)] ${fieldControlFocusStyles}`,
        content: `h-full flex-1 rounded-none border-0 bg-transparent px-[var(--fl-layout-input-fields-padding-horizontal)] py-[var(--fl-layout-input-fields-padding-vertical)] transition-none focus-visible:border-0 focus-visible:outline-none focus-visible:shadow-none disabled:border-0 disabled:bg-transparent`,
      },
    },
    defaultVariants: {
      appearance: "standalone",
    },
  }
)

const fieldControlTextareaStyles = cva(
  `min-h-[100px] w-full select-text resize-y text-ui-12 font-normal placeholder:text-[var(--fl-color-content-default-secondary)] disabled:cursor-not-allowed disabled:pointer-events-none disabled:border-[var(--fl-color-border-outlined-disabled)] disabled:bg-[var(--fl-color-background-outlined-disabled)] disabled:text-[var(--fl-color-content-default-disabled)] disabled:placeholder:text-[var(--fl-color-content-default-disabled)] aria-invalid:border-[var(--fl-color-border-error-default)] group-data-[status=success]/field:border-[var(--fl-color-border-success-default)]`,
  {
    variants: {
      appearance: {
        standalone: `min-h-[100px] rounded-[var(--fl-radius-4)] border border-[var(--fl-color-border-input-default)] bg-[var(--fl-color-background-secondary-default)] px-[var(--fl-layout-input-fields-padding-horizontal)] py-[var(--fl-layout-input-fields-padding-vertical)] transition-[border-color,background-color,box-shadow] hover:border-[var(--fl-color-border-outlined-hover)] ${fieldControlFocusStyles}`,
        // Inside an InputGroup the group owns the 100px minimum (including its border); the textarea fills it.
        content: `min-h-0 flex-1 self-stretch rounded-none border-0 bg-transparent px-[var(--fl-layout-input-fields-padding-horizontal)] py-[var(--fl-layout-input-fields-padding-vertical)] transition-none focus-visible:border-0 focus-visible:outline-none focus-visible:shadow-none disabled:border-0 disabled:bg-transparent`,
      },
    },
    defaultVariants: {
      appearance: "standalone",
    },
  }
)

const fieldControlActionStyles = cva(
  "inline-flex shrink-0 items-center justify-center rounded-[var(--fl-radius-4)] border-0 bg-transparent p-0 text-[var(--fl-color-content-default-secondary)] outline-none transition-colors hover:bg-[var(--fl-color-background-outlined-hover)] hover:text-[var(--fl-color-content-default-default)] focus-visible:outline-none focus-visible:shadow-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-[var(--fl-color-content-default-disabled)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[var(--fl-layout-buttons-icon-size)]",
  {
    variants: {
      size: {
        default: "h-6 gap-1 px-1.5 text-ui-12 font-semibold",
        icon: "size-6 gap-0 text-ui-12",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
)

export {
  fieldControlActionStyles,
  fieldControlFocusStyles,
  fieldControlFocusRingStyles,
  fieldControlInputStyles,
  fieldControlShellStyles,
  fieldControlTextareaStyles,
  updateFieldControlPointer,
}
